import { defineStore } from 'pinia'
import { UserRank } from '~/interfaces/user'
import type {
    LegacyUserData,
    TeslaLinkStatus,
    UserAddress
} from '~/interfaces/user'
import type { UserVehicle } from '#cmui/types/vehicle'
import { $fetchApi } from '~/composables/fetchApi'
import { useNuxtApp } from '#imports'

const useUserStore = defineStore('user', {
    state: () => ({
        userId: 0 as number | null,
        userRank: null as number | null,
        legacyUserData: null as LegacyUserData | null,
        billingAddress: null as UserAddress | null,
        rankLoader: false as boolean,
        userTeslaLinkStatus: null as TeslaLinkStatus | null,
        userAlreadyHasAPass: false as boolean,
        chargemapUsersCount: 0 as number,
        isBoostUser: false as boolean,
        userVehicles: [] as UserVehicle[]
    }),
    getters: {
        getUserRank(state): number[] {
            if (state.userRank === UserRank.ADMIN) {
                return [2, 3]
            }

            return [2]
        },
        isAdmin(state): boolean {
            return state.userRank === UserRank.ADMIN
        }
    },
    actions: {
        setUserId(id: number) {
            this.userId = id
        },
        setUserRank(userRank: number) {
            this.userRank = userRank
        },
        setRankLoader(rankLoader: boolean) {
            this.rankLoader = rankLoader
        },
        setLegacyUserData(data: LegacyUserData) {
            this.legacyUserData = data
        },
        setUserTeslaLinkStatus(userTeslaLinkStatus: TeslaLinkStatus | null) {
            this.userTeslaLinkStatus = userTeslaLinkStatus
        },
        setUserAlreadyHasAPass(userAlreadyHasAPass: boolean) {
            this.userAlreadyHasAPass = userAlreadyHasAPass
        },
        setChargemapUsersCount(chargemapUsersCount: number) {
            this.chargemapUsersCount = chargemapUsersCount
        },
        setIsBoostUser(isBoostUser: boolean) {
            this.isBoostUser = isBoostUser
        },
        async fetchUserRank() {
            const { $throwError } = useNuxtApp()
            const rankUrl = this.legacyUserData?.rank

            if (rankUrl && rankUrl !== 'users/null.json') {
                this.setRankLoader(true)
                try {
                    const data: any = await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/${rankUrl}`
                    )
                    this.setUserRank(data?.response?.content?.id)
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: fetchUserRank (${
                            rankUrl || 'unknown rank'
                        })`
                    )
                }
                this.setRankLoader(false)
            }
        },
        async fetchLegacyUserData() {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    const data: any = await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/users/${this.userId}.json`
                    )
                    this.setLegacyUserData(data?.response?.content)
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: fetchLegacyUserData (${
                            this.userId || 'unknown user'
                        })`
                    )
                    this.setUserId(0)
                }
            }
        },
        async fetchUserAddresses() {
            const { $throwError } = useNuxtApp()

            if (this.userId === 0) {
                return
            }

            try {
                const response = await $fetchApi<{
                    billingAddress: UserAddress | null
                }>(
                    `${useRuntimeConfig().public.APPLICATION_URL}/billy/users/${this.userId}`
                )

                this.billingAddress = response.billingAddress || null
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchUserAddresses (${this.userId})`
                )
            }
        },
        async fetchUserTeslaLinkStatus() {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    const data: any = await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/rfid_passes.json`,
                        { query: { user: this.userId } }
                    )
                    this.setUserTeslaLinkStatus(
                        data?.response?.content?.items[0]?.teslaLinkStatus
                    )
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: fetchUserTeslaLinkStatus (${
                            this.userId || 'unknown user'
                        })`
                    )
                }
            }
        },
        async checkIfUserHasChargemapPass() {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    const data: any = await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community/users/${this.userId}`
                    )
                    this.setUserAlreadyHasAPass(!!data?.defaultRfidPassId)
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: checkIfUserHasChargemapPass (${
                            this.userId || 'unknown user'
                        })`
                    )
                }
            }
        },
        async fetchChargemapUsersCount() {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    current_state: [1, 2, 5, 7],
                    count: true
                }

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/users.json`,
                    {
                        params
                    }
                )
                this.setChargemapUsersCount(data?.response?.content)
            } catch (e: any) {
                $throwError(e, 'ResourceNotFound: fetchChargemapUsersCount')
            }
        },
        async fetchUserVehicles() {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    const data: any = await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community-users/users/${this.userId}/vehicles`,
                        {
                            query: {
                                locale: useLongLocale()
                            }
                        }
                    )

                    this.userVehicles = data?.items || []
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: fetchUserVehicles (${
                            this.userId || 'unknown user'
                        })`
                    )
                }
            }
        },
        async setVehicleAsDefault(userVehicleId: number) {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community-users/users/${this.userId}/vehicles/${userVehicleId}`,
                        {
                            method: 'PATCH',
                            body: {
                                is_default: true
                            }
                        }
                    )

                    await this.fetchUserVehicles()
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: setVehicleAsDefault (${
                            this.userId || 'unknown user'
                        })`
                    )
                }
            }
        },
        async postVehicle(versionId: number) {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community-users/users/${this.userId}/vehicles`,
                        {
                            method: 'POST',
                            body: {
                                vehicle_version_id: versionId
                            }
                        }
                    )

                    await this.fetchUserVehicles()
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: postVehicle (${
                            this.userId || 'unknown user'
                        })`
                    )
                }
            }
        },
        async deleteVehicle(userVehicleId: number, updateUserVehicles = false) {
            const { $throwError } = useNuxtApp()

            if (this.userId) {
                try {
                    await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community-users/users/${this.userId}/vehicles/${userVehicleId}`,
                        {
                            method: 'DELETE'
                        }
                    )

                    if (updateUserVehicles) {
                        await this.fetchUserVehicles()
                    }
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: deleteVehicle (${
                            this.userId || 'unknown user'
                        })`
                    )
                    throw e
                }
            }
        }
    }
})

type UserStore = Omit<
    ReturnType<typeof useUserStore>,
    keyof ReturnType<typeof defineStore>
>

export default useUserStore

export type { UserStore }
